import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { init as initAnalytics, track, utmLeadFields, utmPayload } from "../lib/analytics";
import Captcha, { resetCaptcha } from "../components/Captcha";
import { Button, Input, Label, Field, Select, Card } from "../components/ui/primitives";
import { AddressField } from "../components/ui/AddressField";
import { LogoFull } from "../components/Logo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { Check, ArrowRight, ArrowLeft, Eye, EyeOff, MailCheck, Pencil } from "lucide-react";
import { BrandCopy } from "../components/BrandWordmarkText";
import { getMarketingContent, subscribeMarketingContent } from "../lib/marketingContent";
import MarketingSeo from "../components/marketing/MarketingSeo";
import { copyrightYear } from "../lib/academicYear";
import { CTA_PRIMARY_LABEL } from "../components/marketing/navigation";

// HC-L2: canonical fallback only — the live list is fetched from GET /public/boards (which includes any
// board the platform curates centrally) so the signup form never drifts from the DB board library.
const CANON_BOARDS = ["", "CBSE", "ICSE", "State", "IB"];

const STEPS = [
  { n: 1, label: "Create your account" },
  { n: 2, label: "School details" },
  { n: 3, label: "Verify & create" },
];

// Only these keys leave the browser (the backend's RegisterTenantRequest contract).
const PAYLOAD_KEYS = [
  "school_name", "owner_name", "email", "password", "phone", "city",
  "school_code", "board", "academic_year", "udise_code", "principal_name", "legal_name",
  "postal_code", "state", "district", "locality", "address_line1", "address_line2", "landmark",
  "terms_accepted", "marketing_opt_in", // WP-11 — persisted server-side with timestamp + policy version
];

export default function Register() {
  const { register, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(() => {
    // Prefill from the Landing "Register your school" starter (/register?school_name=…&owner_name=…&email=…)
    const q = new URLSearchParams(window.location.search);
    return {
      school_name: q.get("school_name") || "", owner_name: q.get("owner_name") || "", email: q.get("email") || "",
      password: "", phone: q.get("phone") || "", city: q.get("city") || "",
      // ONB-08/09 optional school profile — completable later in Settings → School Profile
      school_code: "", board: "", academic_year: "", udise_code: "", principal_name: "", legal_name: "",
      // structured India-first address (shared AddressField keys)
      postal_code: "", state: "", district: "", locality: "", address_line1: "", address_line2: "", landmark: "",
      // WP-11 — explicit, unticked by default; terms gates step 1, marketing is separate + optional
      terms_accepted: false, marketing_opt_in: false,
    };
  });
  const [step, setStep] = useState(1);
  // Initialise campaign analytics for direct /register visits (idempotent and UTM-aware).
  useEffect(() => { initAnalytics(); }, []);
  const [boards, setBoards] = useState(CANON_BOARDS);   // HC-L2: fetched from /public/boards on mount
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  // ONB R2-A: signup email verification — revealed when the backend answers 428 verification_required.
  const [verifyStep, setVerifyStep] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [devOtp, setDevOtp] = useState(""); // dev-mode code from /auth/register/start (DEV_EXPOSE_RESET_TOKEN)
  const [resendLeft, setResendLeft] = useState(0); // client-side resend throttle (seconds)
  const [resending, setResending] = useState(false);
  // Founder spec A (SCHOOL_APPROVAL_REQUIRED): the school parked in the platform verification queue.
  const [pendingMsg, setPendingMsg] = useState("");
  // Live School-Code availability (debounced against the public check endpoint)
  const [codeCheck, setCodeCheck] = useState(null); // {code, valid, available, reason}
  const codeTimer = useRef(null);
  const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const set = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => (er[k] ? { ...er, [k]: "" } : er));
  };
  const patchAddress = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    const code = form.school_code.trim();
    setCodeCheck(null);
    if (!code) return undefined;
    clearTimeout(codeTimer.current);
    codeTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/public/school-code/check", { params: { code } });
        setCodeCheck(data);
      } catch {
        setCodeCheck(null); // availability is a nicety — the server re-validates at submit
      }
    }, 400);
    return () => clearTimeout(codeTimer.current);
  }, [form.school_code]);

  // Tick the resend cooldown down once a second while it's running.
  useEffect(() => {
    if (!resendLeft) return undefined;
    const id = setTimeout(() => setResendLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearTimeout(id);
  }, [resendLeft]);

  // HC-L2: pull the canonical board list (CANON + platform-curated global library) — keeps signup in
  // sync with the DB board library. Falls back to the canonical constant if the fetch fails.
  useEffect(() => {
    let alive = true;
    api.get("/public/boards").then(({ data }) => {
      if (alive && Array.isArray(data.boards) && data.boards.length) setBoards(["", ...data.boards]);
    }).catch(() => { /* keep CANON fallback */ });
    return () => { alive = false; };
  }, []);

  // The step just changed — focus targets only exist after the re-render.
  const focusTid = (tid) => setTimeout(() => document.querySelector(`[data-testid="${tid}"]`)?.focus(), 0);

  const validateStep1 = () => {
    const next = {};
    if (!form.school_name.trim()) next.school_name = "Enter your school's name.";
    if (!form.owner_name.trim()) next.owner_name = "Enter your name.";
    if (!emailValid(form.email)) next.email = "Enter a valid email address, for example principal@yourschool.in.";
    if (form.password.length < 8) next.password = "Password must be at least 8 characters.";
    if (!form.terms_accepted) next.terms_accepted = "Please accept the Terms of Service and Privacy Policy to continue.";
    setErrors((er) => ({ ...er, ...next }));
    const firstInvalid = [
      ["school_name", "reg-school"],
      ["owner_name", "reg-name"],
      ["email", "reg-email"],
      ["password", "reg-password"],
      ["terms_accepted", "reg-terms"],
    ].find(([k]) => next[k]);
    if (firstInvalid) {
      setStep(1);
      focusTid(firstInvalid[1]);
      return false;
    }
    return true;
  };

  const validateSchoolCode = () => {
    if (form.school_code.trim() && codeCheck && (!codeCheck.valid || !codeCheck.available)) {
      setStep(2);
      setErrors((er) => ({ ...er, school_code: codeCheck.reason || "Pick a different school code." }));
      focusTid("reg-code");
      return false;
    }
    return true;
  };

  // Stepper navigation: back is always free; 1→2 is free (school details are optional/skippable);
  // landing on the review step re-checks the earlier steps.
  const goToStep = (n) => {
    if (n === step || loading) return;
    setError("");
    if (n < step) { setStep(n); return; }
    if (n === 3 && (!validateStep1() || !validateSchoolCode())) return;
    // CRO-13: skipping ("fill it later") or stepper-jumping past step 2 still completes it.
    if (n === 3 && step === 2) track("registration_step2_completed", { placement: "register_page" });
    setStep(n);
  };

  const resendCode = async () => {
    if (resendLeft > 0 || resending) return;
    setResending(true);
    try {
      const { data } = await api.post("/auth/register/start", { email: form.email, captcha_token: captchaToken });
      resetCaptcha(); // tokens are single-use
      setDevOtp(data?.dev_otp || "");
      setResendLeft(30);
      setError("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setResending(false); }
  };

  const doRegister = async () => {
    if (!validateStep1() || !validateSchoolCode()) return;
    if (verifyStep && verifyCode.trim().length !== 6) {
      setError("Enter the 6-digit code we sent to your email.");
      focusTid("reg-verify-code");
      return;
    }
    setLoading(true);
    try {
      // Send only filled optional fields (the backend treats them all as optional).
      const payload = {};
      for (const k of PAYLOAD_KEYS) {
        const v = form[k];
        if (typeof v === "string" ? v.trim() !== "" : v != null) payload[k] = v;
      }
      // CRO-7: acquisition attribution on the trial itself — the SAME utmLeadFields() derivation
      // every public lead surface uses, mapped onto the register contract's bounded optional
      // fields (signup_campaign 80 / signup_source 40; whitespace-normalised and stored on the
      // tenant server-side). Consent-gated at the source: utmPayload() returns {} without
      // optional-analytics consent, so a visitor who declined sends nothing. Set AFTER the form
      // loop so a form key can never be clobbered.
      const { campaign: utmCampaign } = utmLeadFields();
      if (utmCampaign) payload.signup_campaign = utmCampaign;
      const utmSource = utmPayload().utm_source;
      if (utmSource) payload.signup_source = String(utmSource).slice(0, 40);
      if (captchaToken) payload.captcha_token = captchaToken;
      if (verifyStep) payload.verification_code = verifyCode.trim(); // same payload + the code
      const res = await register(payload);
      // conversion: the school account was actually created (or queued for verification).
      // CRO-2: `trial_start` fires ONLY here — every marketing "Start Free Trial" CLICK fires
      // `trial_cta_click {placement}` instead, so one pricing→register journey can no longer
      // double-count the same event name. (GA4 key-event config: keep trial_start as the
      // conversion; register trial_cta_click as a plain engagement event.)
      track("trial_start", { placement: "register_page" });
      track("registration_completed", { placement: "register_page" }); // WP-06 canonical conversion
      if (res?.pending_verification) {
        // Verification-queue mode: no session yet — show the submitted-for-verification panel.
        // TRUST-8: byte-mirrors routers/auth.py `_pending_school_verification_response` — states
        // when we work, never a turnaround time (claims-registry S10). Edit both together.
        setPendingMsg(res.message || "Thanks! Your school has been submitted for verification. We'll email you as soon as Vidyapeeth360 approves it — our team reviews new school registrations on working days.");
        return;
      }
      // TRUST-13: the 90-day trial NEVER auto-converts (P4) — paid service needs an explicit
      // plan selection + mandate/checkout at expiry. Land on onboarding, not billing.
      navigate("/app/onboarding?welcome=1");
    } catch (err) {
      // ONB R2-A: SIGNUP_VERIFICATION_REQUIRED — send the code, then reveal the verification step.
      if (err.response?.status === 428 && err.response?.data?.detail === "verification_required") {
        // CRO-13: the verification step is a real funnel stage — make its drop-off measurable.
        track("registration_verification_required", { placement: "register_page" });
        try {
          resetCaptcha(); // mint a fresh token — the register attempt consumed the previous one
          const { data } = await api.post("/auth/register/start", { email: form.email });
          setVerifyStep(true);
          setVerifyCode("");
          setDevOtp(data?.dev_otp || "");
          setResendLeft(30);
          focusTid("reg-verify-code");
        } catch (e2) {
          // CRO-5: a hard failure in the register flow must be analytically visible.
          track("registration_failed", { placement: "register_page", http_status: e2?.response?.status ?? 0 });
          setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
        }
      } else {
        track("registration_failed", { placement: "register_page", http_status: err?.response?.status ?? 0 }); // CRO-5
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    } finally {
      resetCaptcha(); // the attempt consumed the token either way
      setLoading(false);
    }
  };

  // Enter (or the primary button) advances the wizard; the real POST only happens on step 3.
  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    if (step === 1) {
      if (validateStep1()) {
        track("registration_step1_completed", { placement: "register_page" }); // WP-06 funnel step
        setStep(2);
      }
      return;
    }
    if (step === 2) {
      if (validateSchoolCode()) {
        track("registration_step2_completed", { placement: "register_page" }); // CRO-13 funnel step
        setStep(3);
      }
      return;
    }
    await doRegister();
  };

  // The trial length is GOVERNED data (standard trial policy, or a live extended-trial campaign —
  // "6 months free instead of 90 days"). This page grants whatever the server grants at signup, so
  // its copy must state the same number: read `pricing_integrity.trial` from the shared marketing
  // payload and substitute the standard phrase, exactly like the server-side sweep does for CMS
  // copy. Fail-safe: no payload → the standard 90-day wording.
  const [trialInfo, setTrialInfo] = useState(null);
  useEffect(() => {
    let on = true;
    getMarketingContent()
      .then((d) => { if (on) setTrialInfo(d?.pricing_integrity?.trial || null); })
      .catch(() => {});
    // Stay LIVE: a prerender-bootstrap payload can be older than the campaign toggle, and a page
    // promising "6 months free" after the offer ended is exactly the misleading-copy exposure the
    // whole feature guards against. The shared revalidation broadcasts fresh payloads here.
    const unsubscribe = subscribeMarketingContent((d) => {
      if (on) setTrialInfo(d?.pricing_integrity?.trial || null);
    });
    return () => { on = false; if (typeof unsubscribe === "function") unsubscribe(); };
  }, []);
  const trialNoun = trialInfo?.campaign_months ? `${trialInfo.campaign_months} months` : "90 days";
  const trialAdj = trialInfo?.campaign_months ? `${trialInfo.campaign_months}-month` : "90-day";
  // No regex lookbehind (Safari < 16.4, inside the build's browser floor): capture-and-restore
  // via a replacer FUNCTION (a "$1" string literal trips the India-first currency guard).
  const withTrial = (text) => text
    .replace(/(^|[^\d₹])90-day\b/g, (hit, lead) => lead + trialAdj)
    .replace(/(^|[^\d₹])90 days\b/g, (hit, lead) => lead + trialNoun);

  const perks = [
    `${trialAdj} free trial for every school`,
    "Import your student list from Excel with AI-assisted column mapping",
    "Admissions CRM, fees & WhatsApp comms",
    // PGS-9: Aira is a Premium-tier feature — the trial carries it; the paid plan decides after.
    `Aira AI assistant included in your ${trialAdj} trial`,
  ];

  const codeOk = codeCheck && form.school_code && codeCheck.valid && codeCheck.available;
  const summaryRows = [
    ["School", form.school_name],
    ["Your name", form.owner_name],
    ["Work email", form.email],
    ["School code", form.school_code || "— pick later in Settings"],
    ["Board", form.board || "—"],
    ["City", form.city || form.district || "—"],
  ];

  return (
    <div className="vp-themed min-h-screen flex">
      {/* WP-10: /register previously set NO head metadata at runtime — an SPA navigation here
          carried the previous route's title/description. Copy mirrors prerender ROUTE_META. */}
      <MarketingSeo
        title="Register Your School — Free Trial | Vidyapeeth360"
        description="Create your school workspace, choose a School Code and board, and start your free trial — no credit card needed."
        path="/register"
      />
      <div className="fixed top-4 right-4 z-50"><LanguageSwitcher /></div>
      <aside className="hidden lg:flex w-[44%] bg-royal relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute inset-0 bg-grid opacity-30" aria-hidden="true" />
        <div className="absolute -bottom-20 -left-16 w-80 h-80 rounded-full border border-informative/20" aria-hidden="true" />
        <div className="relative">
          <LogoFull height={46} />
        </div>
        <div className="relative max-w-md">
          <p className="font-heading font-bold text-[32px] leading-tight text-white">Create your school workspace in about three minutes.</p>
          {/* PGS-9/S11: no implied customer-base claim — say what the platform DOES. */}
          <p className="text-white/70 mt-4 text-[15px]">Run admissions, fees and parent communication in one place — with Aira drafting the routine follow-ups.</p>
          <ul className="mt-8 space-y-3.5">
            {perks.map((p) => (
              <li key={p} className="flex items-center gap-3 text-white/85 text-sm">
                <span className="w-6 h-6 rounded-full bg-teal/20 grid place-items-center shrink-0">
                  <Check className="w-3.5 h-3.5 text-teal" aria-hidden="true" />
                </span>
                {p}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative text-white/60 text-xs"><BrandCopy light>{`© ${copyrightYear()} Vidyapeeth360`}</BrandCopy></div>
      </aside>

      <main className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-surface overflow-y-auto">
        {pendingMsg ? (
          // Founder spec A — the school is parked in the platform verification queue (no session yet).
          <div className="w-full max-w-[480px] py-6" data-testid="reg-pending-panel">
            <div className="lg:hidden mb-8 flex justify-center"><LogoFull height={40} /></div>
            <div className="w-14 h-14 rounded-2xl bg-success/10 grid place-items-center mb-5">
              <MailCheck className="w-7 h-7 text-success" aria-hidden="true" />
            </div>
            <h1 className="font-heading font-bold text-[26px] text-ink">Thanks! Your school has been submitted for verification</h1>
            <p role="status" className="text-ink-2 text-sm mt-3"><BrandCopy>{pendingMsg}</BrandCopy></p>
            <Card className="mt-6 p-5">
              <p className="text-sm font-semibold text-ink">What happens next</p>
              <ol className="mt-3 space-y-2.5 text-sm text-ink-2 list-none">
                {[
                  // TRUST-8: no turnaround promise — verification is a human review in the
                  // platform console, and nothing here can measure or honour a stated time.
                  "Our team verifies your school's details — reviews happen on working days.",
                  <>We email you at <span className="font-semibold text-ink">{form.email}</span> the moment it's approved.</>,
                  withTrial("Sign in with your email and password — your 90-day free trial starts right away."),
                ].map((t, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-accent-active text-[11px] font-bold grid place-items-center shrink-0 mt-0.5">{i + 1}</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ol>
            </Card>
            <div className="mt-6 flex items-center gap-3">
              <Button onClick={() => navigate("/login")} data-testid="reg-pending-login">Go to sign in</Button>
              <Link to="/" className="text-sm font-semibold text-accent hover:underline rounded-sm focus-visible:outline-none focus-visible:ring-2 ring-primary">Back to home</Link>
            </div>
          </div>
        ) : (
        <div className="w-full max-w-[460px] py-6">
          <div className="lg:hidden mb-8 flex justify-center"><LogoFull height={40} /></div>
          <h1 id="reg-title" className="font-heading font-bold text-[26px] text-ink">Create your school account</h1>
          <p className="text-ink-subtle text-sm mt-1">You'll be the school administrator for this workspace.</p>

          {/* Stepper — numbered, clickable, aria-current on the active step. The step-2 button keeps
              the historical reg-more-toggle testid (it's still "the control that opens school details"). */}
          <nav aria-label="Registration steps" className="mt-6">
            <ol className="flex items-stretch gap-1.5 sm:gap-2">
              {STEPS.map((s) => {
                const isActive = step === s.n;
                const isDone = step > s.n;
                return (
                  <li key={s.n} className="flex-1 min-w-0">
                    <button
                      type="button"
                      onClick={() => goToStep(s.n)}
                      aria-current={isActive ? "step" : undefined}
                      aria-label={`Step ${s.n} of 3: ${s.label}`}
                      data-testid={s.n === 2 ? "reg-more-toggle" : `reg-step-${s.n}`}
                      className={`w-full h-full flex sm:flex-col items-center sm:items-start gap-2 sm:gap-1.5 rounded-xl border px-2.5 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 ring-primary ${
                        isActive ? "border-primary/40 bg-primary/5" : "border-line hover:bg-muted"}`}>
                      <span aria-hidden="true" className={`w-6 h-6 rounded-full grid place-items-center text-xs font-bold shrink-0 ${
                        isActive ? "bg-primary text-white" : isDone ? "bg-success/15 text-success" : "bg-muted text-ink-subtle"}`}>
                        {isDone ? <Check className="w-3.5 h-3.5" aria-hidden="true" /> : s.n}
                      </span>
                      <span className={`hidden sm:block text-xs leading-tight truncate w-full ${isActive ? "font-semibold text-ink" : "text-ink-subtle"}`}>
                        {s.label}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
            <div className="mt-2 h-1 rounded-full bg-muted" aria-hidden="true">
              <div className="h-1 rounded-full bg-primary transition-[width] duration-300" style={{ width: `${(step / STEPS.length) * 100}%` }} />
            </div>
            <p className="sm:hidden mt-2 text-xs font-semibold text-ink-2">Step {step} of {STEPS.length} — {STEPS[step - 1].label}</p>
          </nav>

          <form onSubmit={submit} noValidate aria-labelledby="reg-title" className="mt-6 space-y-4">
            {step === 1 && (
              <div className="space-y-4">
                <Field label="School name" required error={errors.school_name || undefined}>
                  <Input data-testid="reg-school" autoFocus autoComplete="organization" value={form.school_name} onChange={set("school_name")} onBlur={() => setErrors((er) => ({ ...er, school_name: form.school_name.trim() ? "" : "Enter your school's name." }))} error={!!errors.school_name} placeholder="Your school's name" required />
                </Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Your name" required error={errors.owner_name || undefined}>
                    <Input data-testid="reg-name" autoComplete="name" value={form.owner_name} onChange={set("owner_name")} onBlur={() => setErrors((er) => ({ ...er, owner_name: form.owner_name.trim() ? "" : "Enter your name." }))} error={!!errors.owner_name} placeholder="Full name" required />
                  </Field>
                  <Field label="City">
                    <Input data-testid="reg-city" autoComplete="address-level2" value={form.city} onChange={set("city")} placeholder="Pune" />
                  </Field>
                </div>
                <Field label="Work email" required error={errors.email || undefined}>
                  <Input data-testid="reg-email" type="email" autoComplete="email" value={form.email}
                    onChange={set("email")}
                    onBlur={() => setErrors((er) => ({ ...er, email: form.email && !emailValid(form.email) ? "Enter a valid email address, for example principal@yourschool.in." : "" }))}
                    error={!!errors.email} placeholder="principal@yourschool.in" required />
                </Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Phone">
                    <Input data-testid="reg-phone" type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={set("phone")} placeholder="+91 98765 43210" />
                  </Field>
                  <div>
                    <Label htmlFor="reg-password">Password</Label>
                    <div className="relative">
                      <Input id="reg-password" data-testid="reg-password" type={showPw ? "text" : "password"} autoComplete="new-password" value={form.password}
                        onChange={set("password")}
                        onBlur={() => setErrors((er) => ({ ...er, password: form.password && form.password.length < 8 ? "Password must be at least 8 characters." : "" }))}
                        error={!!errors.password} placeholder="At least 8 characters" required
                        aria-invalid={errors.password ? true : undefined} aria-describedby={errors.password ? "reg-password-err" : undefined} />
                      <button type="button" aria-label={showPw ? "Hide password" : "Show password"} onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 grid place-items-center p-1.5 -mr-1.5 rounded text-ink-subtle hover:text-ink transition-colors focus-visible:outline-none focus-visible:ring-2 ring-primary">
                        {showPw ? <EyeOff className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
                      </button>
                    </div>
                    {errors.password && <p id="reg-password-err" className="text-xs text-critical mt-1.5">{errors.password}</p>}
                  </div>
                </div>
                {/* WP-11 — DPDP notice at (before) collection: an explicit, UNTICKED acceptance
                    checkbox gates Next, and marketing opt-in is a SEPARATE optional choice. */}
                <div className="space-y-2.5 pt-1">
                  <div className="flex items-start gap-2.5">
                    <input
                      id="reg-terms" data-testid="reg-terms" type="checkbox" checked={form.terms_accepted}
                      onChange={(e) => { const v = e.target.checked; setForm((f) => ({ ...f, terms_accepted: v })); setErrors((er) => ({ ...er, terms_accepted: "" })); }}
                      aria-invalid={errors.terms_accepted ? true : undefined}
                      aria-describedby={errors.terms_accepted ? "reg-terms-err" : undefined}
                      className="mt-0.5 h-4.5 w-4.5 h-[18px] w-[18px] shrink-0 rounded border-line text-primary focus-visible:ring-2 focus-visible:ring-primary"
                    />
                    <label htmlFor="reg-terms" className="text-[13px] leading-relaxed text-ink-2">
                      I agree to the <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="font-semibold text-accent hover:underline">Terms of Service</Link> and{" "}
                      <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" className="font-semibold text-accent hover:underline">Privacy Policy</Link>.
                    </label>
                  </div>
                  {errors.terms_accepted && <p id="reg-terms-err" className="text-xs text-critical" role="alert">{errors.terms_accepted}</p>}
                  <div className="flex items-start gap-2.5">
                    <input
                      id="reg-marketing" data-testid="reg-marketing" type="checkbox" checked={form.marketing_opt_in}
                      onChange={(e) => { const v = e.target.checked; setForm((f) => ({ ...f, marketing_opt_in: v })); }}
                      className="mt-0.5 h-[18px] w-[18px] shrink-0 rounded border-line text-primary focus-visible:ring-2 focus-visible:ring-primary"
                    />
                    <label htmlFor="reg-marketing" className="text-[13px] leading-relaxed text-ink-subtle">
                      Email me product updates and school-operations tips (optional).
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* ONB-08/09 — optional school profile: the School Code + India-K12 anchor fields.
                Everything here is optional at signup and editable later in Settings → School Profile. */}
            {step === 2 && (
              <div className="space-y-4">
                <p className="text-sm text-ink-subtle">All of this is optional — you can <button type="button" data-testid="reg-skip" onClick={() => goToStep(3)} className="font-semibold text-accent hover:underline rounded-sm focus-visible:outline-none focus-visible:ring-2 ring-primary">fill it later in Settings</button>.</p>
                <Field label="School code" hint="Parents and students use this code to register. 3–30 characters: lowercase letters, numbers and hyphens."
                  error={errors.school_code || (codeCheck && form.school_code && (!codeCheck.valid || !codeCheck.available) ? codeCheck.reason : undefined)}>
                  <Input data-testid="reg-code" value={form.school_code} onChange={set("school_code")} placeholder="sunrise-public-school" />
                  {codeOk && (
                    <p className="text-xs text-success mt-1.5 flex items-center gap-1">
                      <Check className="w-3.5 h-3.5" aria-hidden="true" /> {codeCheck.code} is available
                    </p>
                  )}
                </Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Board">
                    <Select data-testid="reg-board" value={form.board} onChange={set("board")}>
                      {boards.map((b) => <option key={b} value={b}>{b || "Select board"}</option>)}
                    </Select>
                  </Field>
                  <Field label="Academic year" hint="e.g. 2026-27">
                    <Input value={form.academic_year} onChange={set("academic_year")} placeholder="2026-27" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="UDISE+ school code" hint="11 digits">
                    <Input inputMode="numeric" value={form.udise_code} onChange={set("udise_code")} placeholder="27251234567" />
                  </Field>
                  <Field label="Principal's name">
                    <Input value={form.principal_name} onChange={set("principal_name")} placeholder="Dr. Meena Rao" />
                  </Field>
                </div>
                <Field label="Trust / society name" hint="The legal entity that runs the school">
                  <Input value={form.legal_name} onChange={set("legal_name")} placeholder="Registered trust / society name" />
                </Field>
                <fieldset className="border border-line rounded-xl p-4 pt-3">
                  <legend className="px-1.5 text-sm font-semibold text-ink">School address</legend>
                  <AddressField publicMode value={form} onChange={patchAddress} />
                </fieldset>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <Card className="p-4" data-testid="reg-review">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-ink">Review your details</p>
                    <div className="flex items-center gap-3">
                      <button type="button" data-testid="reg-edit-account" onClick={() => goToStep(1)} className="inline-flex items-center gap-1 text-xs font-semibold text-accent hover:underline rounded-sm focus-visible:outline-none focus-visible:ring-2 ring-primary">
                        <Pencil className="w-3 h-3" aria-hidden="true" /> Account
                      </button>
                      <button type="button" data-testid="reg-edit-school" onClick={() => goToStep(2)} className="inline-flex items-center gap-1 text-xs font-semibold text-accent hover:underline rounded-sm focus-visible:outline-none focus-visible:ring-2 ring-primary">
                        <Pencil className="w-3 h-3" aria-hidden="true" /> School details
                      </button>
                    </div>
                  </div>
                  <dl className="mt-3 space-y-2">
                    {summaryRows.map(([k, v]) => (
                      <div key={k} className="flex items-baseline justify-between gap-4 text-sm">
                        <dt className="text-ink-subtle shrink-0">{k}</dt>
                        <dd className="text-ink font-medium text-right break-words min-w-0">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </Card>
                <p className="text-sm text-ink-2">Your <span className="font-semibold text-ink">{trialAdj} free trial</span> starts the moment your school is created — no card needed.</p>

                {/* ONB R2-A — signup verification step (revealed on the backend's 428) */}
                {verifyStep && (
                  <div className="rounded-xl border border-primary/25 bg-primary/5 p-4 space-y-3" data-testid="reg-verify-step">
                    <p role="status" className="text-sm text-ink-2">
                      We sent a 6-digit code to <span className="font-semibold text-ink">{form.email}</span>. It expires in 15 minutes — enter it below to verify your email.
                    </p>
                    <Field label="Verification code" required>
                      <Input data-testid="reg-verify-code" inputMode="numeric" autoComplete="one-time-code"
                        maxLength={6} value={verifyCode} placeholder="123456"
                        onChange={(e) => { setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6)); if (error) setError(""); }} />
                    </Field>
                    {process.env.NODE_ENV !== "production" && devOtp && (
                      <p className="text-xs text-ink-subtle" data-testid="reg-verify-dev">Dev code: <b>{devOtp}</b></p>
                    )}
                    <button type="button" onClick={resendCode} disabled={resendLeft > 0 || resending}
                      className="text-sm font-semibold text-accent hover:underline disabled:text-ink-subtle disabled:no-underline disabled:cursor-not-allowed rounded-sm focus-visible:outline-none focus-visible:ring-2 ring-primary"
                      data-testid="reg-verify-resend">
                      {resendLeft > 0 ? `Resend code in ${resendLeft}s` : resending ? "Sending…" : "Resend code"}
                    </button>
                  </div>
                )}
              </div>
            )}

            {error && <div data-testid="reg-error" role="alert" className="text-sm text-critical bg-critical-soft border border-critical/20 rounded-xl px-3.5 py-2.5">{error}</div>}

            {/* Password strength (simple heuristic — length + character mix) */}
            {step === 3 && form.password && (() => {
              const pw = form.password;
              const score = (pw.length >= 8 ? 1 : 0) + (pw.length >= 12 ? 1 : 0)
                + (/[A-Z]/.test(pw) && /[a-z]/.test(pw) ? 1 : 0) + (/\d/.test(pw) ? 1 : 0) + (/[^A-Za-z0-9]/.test(pw) ? 1 : 0);
              const label = score <= 1 ? "Weak" : score <= 3 ? "Good" : "Strong";
              const tone = score <= 1 ? "bg-critical" : score <= 3 ? "bg-caution" : "bg-positive";
              return (
                <div aria-live="polite">
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden" aria-hidden="true">
                    <div className={`h-full rounded-full transition-[width,background-color] ${tone}`} style={{ width: `${Math.min(100, score * 20)}%` }} />
                  </div>
                  <p className="text-[11.5px] text-ink-subtle mt-1">Password strength: <span className="font-semibold text-ink-2">{label}</span> — 8+ characters; mix cases, numbers and symbols for Strong.</p>
                </div>
              );
            })()}

            {/* Anti-bot verification (renders only when Turnstile keys are configured) */}
            {step === 3 && <Captcha onToken={setCaptchaToken} action="signup" className="flex justify-center" />}

            {/* Signup legal consent (clickwrap-lite — the data-collection point) */}
            {step === 3 && (
              <p className="text-[12px] text-ink-subtle text-center">
                By creating your school workspace you agree to our{" "}
                <Link to="/terms-and-conditions" className="text-accent font-medium hover:underline" target="_blank" rel="noopener noreferrer">Terms</Link> and{" "}
                <Link to="/privacy-policy" className="text-accent font-medium hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
              </p>
            )}

            {/* Wizard actions — sticky above the fold on small screens */}
            <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-surface/95 backdrop-blur border-t border-line sm:static sm:mx-0 sm:px-0 sm:py-0 sm:bg-transparent sm:border-0 flex items-center gap-3">
              {step > 1 && (
                <Button type="button" variant="outline" onClick={() => goToStep(step - 1)} data-testid="reg-back">
                  <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back
                </Button>
              )}
              {step < 3 ? (
                <Button type="submit" className="flex-1" size="lg" data-testid="reg-next">
                  Next <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Button>
              ) : (
                <Button type="submit" loading={loading} className="flex-1" size="lg" data-testid="reg-submit">
                  {verifyStep ? "Verify & create school" : <>{CTA_PRIMARY_LABEL} <ArrowRight className="w-4 h-4" aria-hidden="true" /></>}
                </Button>
              )}
            </div>
          </form>

          <p className="text-center text-sm text-ink-subtle mt-6">
            Already have an account? <Link to="/login" className="font-semibold text-accent hover:underline rounded-sm focus-visible:outline-none focus-visible:ring-2 ring-primary">Sign in</Link>
          </p>
        </div>
        )}
      </main>
    </div>
  );
}
